import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { decodeAppointmentData } from "../../utils/prefillUtils.js";
import { Calendario } from "./Calendario"; // Seu componente Calendario
import { AgendamentoModal } from "./AgendamentoModal";
import BulkAppointmentsModal from "./BulkAppointmentsModal.js";
import style from './CalendarioApp.module.css';
import './CalendarioAppFCs.css'
import addToHead from "../ferramentas/AddToHead";
import { isInt } from "@fullcalendar/core/internal";
import { useDataManagement } from "../ferramentas/RequisitarAPI.ts"; // Seu hook de API
import { ChecarData } from "../ferramentas/checarData/checarData";
import ToastMessage from "../ferramentas/toastMessage/ToastMessage";
import ModalConfirmaExclusao from "../ferramentas/modais/modalConfirmaExclusao/modalConfirmaExclusao";
import Rightbar from "./Rightbar.js";
import { Sidebar } from "./Sidebar.js";
import SearchBar from "./SearchBar.js";
import ServicoModal from "./ServicoModal.js";
import UsuarioModal from "./UsuarioModal.js";
import UsersManage from "./UsersManage.js";
import Select from 'react-select';

// Função utilitária para verificar se duas datas são o mesmo dia - movida para fora do componente
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

const calcularGradiente = (services) => {
  if (!services || Object.keys(services).length === 0) return "linear-gradient(180deg, #ffffff 0%, #ffffff 100%)";

  const listaPorcentagensECores = Object.entries(services).map(([id, service]) => ({
    porcentagem: service.percent,
    cor: service.color
  }));

  const porcentagensOrdenadas = listaPorcentagensECores.sort((a, b) => a.porcentagem - b.porcentagem);

  if (porcentagensOrdenadas.length === 1) {
    return `linear-gradient(180deg, ${porcentagensOrdenadas[0].cor} 0%, ${porcentagensOrdenadas[0].cor} 100%)`;
  }

  let linearGradient = "linear-gradient(180deg";
  let acumulador = 0;

  porcentagensOrdenadas.forEach((item) => {
    linearGradient += `, ${item.cor} ${(item.porcentagem / 2) + acumulador}%`;
    acumulador += item.porcentagem;
  });

  return linearGradient + ")";
};

const CalendarioApp = forwardRef((props, ref) => {
  const [appointments, setAppointments] = useState({});
  const [accessLevel, setAccessLevel] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const {
    appointments: fetchAppointments,
    services: fetchServices,
    users: fetchUsers,
    load
  } = useDataManagement();
  const [atualizar, setAtualizar] = useState(false);
  const modalRef = useRef(null);
  const [modalServicoOpen, setModalServicoOpen] = useState(false);
  const [modalAgendamentoOpen, setModalAgendamentoOpen] = useState(false);
  const [isBulkModalOpen, setBulkModalOpen] = useState(false);
  const [servicoData, setServicoData] = useState(null);
  const [modalUsuarioOpen, setModalUsuarioOpen] = useState(false);
  const [modalGerenciarUsuarioOpen, setModalGerenciarUsuarioOpen] = useState(false);
  const [usuarioData, setUsuarioData] = useState(null);
  const [larguraTela, setLarguraTela] = useState(window.innerWidth);
  const [menuAgendamentosAtivo, setAgendamentosActive] = useState(false);
  const [showAgendamentos, setShowAgendamentos] = useState(false);
  const isInitialMount = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedUserFilter, setSelectedUserFilter] = useState({ value: null, label: "Todos" });
  const [userFiltersOptions, setUserFiltersOptions] = useState([]);

  // Estado para armazenar dados de pré-preenchimento vindos da URL
  const [prefillData, setPrefillData] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isUserFilterFocused, setIsUserFilterFocused] = useState(false);

  // Armazena o mês atual exibido no FullCalendar, sempre fixado no 1º dia
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  useEffect(() => {
    const fcStyleLink = addToHead("link", {
      href: "./CalendarioAppFCs.css",
      rel: "stylesheet",
    });
    return () => {
      document.head.removeChild(fcStyleLink);
    };
  }, []);

  // Processa query params para pré-preenchimento de agendamento
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefillParam = searchParams.get('prefill');

    if (prefillParam) {
      const decoded = decodeAppointmentData(prefillParam);
      if (decoded) {
        // Define os dados de prefill
        setPrefillData(decoded);

        // Navega para o mês da data do agendamento
        const appointmentDate = new Date(decoded.date);
        if (!isNaN(appointmentDate.getTime())) {
          setCurrentCalendarMonth(
            new Date(appointmentDate.getUTCFullYear(), appointmentDate.getUTCMonth(), 1)
          );
          setSelectedDate([appointmentDate]);
          setModalAgendamentoOpen(true);
        }

        // Limpa a URL para não reabrir o modal no refresh
        navigate('/admin/calendario', { replace: true });
      }
    }
  }, [location.search, navigate]);

  // Carregamento de dados - memoizado para evitar recálculos
  const loadAppointments = useCallback(async () => {
    try {
      // Garante que estamos utilizando o primeiro dia do mês atualmente exibido
      const firstDayOfMonth = new Date(
        currentCalendarMonth.getFullYear(),
        currentCalendarMonth.getMonth(),
        1
      );

      const month = firstDayOfMonth.getMonth() + 1; // +1 porque getMonth() é 0-indexed (0-11)
      const year = firstDayOfMonth.getFullYear();



      //

      // Formata as datas para YYYY-MM-DD
      // const formatDate = (date) => date.toISOString().split('T')[0];

      // const startDateParam = formatDate(firstDayOfMonth);

      // Passa start_date e end_date como query parameters para a API
      const userParam = (["admin", "mod"].includes(accessLevel) && selectedUserFilter && selectedUserFilter.value) ? selectedUserFilter.value : undefined;
      const agendamentos = await fetchAppointments.listMonth('month=' + month, year, userParam);

      // if (agendamentos.aborted) {
      //   return;
      // }

      if (agendamentos.ok && agendamentos.data) {
        const processedAppointments = {};
        Object.entries(agendamentos.data).forEach(([id, appointment]) => {
          processedAppointments[id] = {
            ...appointment,
            linearGradient: calcularGradiente(appointment.services),
            groupIcon: appointment.group_qtde > 1,
          };
        });

        setAppointments(processedAppointments);

      } else if (agendamentos.message) {
        ToastMessage({ cod: "e", texto: agendamentos.message || "Erro ao carregar agendamentos FE" });
        console.error("loadAppointments: Erro da API ao carregar agendamentos:", agendamentos.message);
      }
    } catch (error) {
      console.error("loadAppointments: Erro ao carregar agendamentos:", error);
      if (!(error?.name === 'AbortError')) {
        ToastMessage({ cod: "e", texto: "Ocorreu um erro ao carregar os agendamentos" });
      }
    }
  }, [fetchAppointments, currentCalendarMonth, selectedUserFilter, accessLevel]);

  const setAccessLevelFunc = useCallback(async () => {
    try {
      const result = await fetchUsers.getCurrentUser();
      if (result.aborted) {
        return;
      }
      if (result.ok) {
        setAccessLevel(result.data.access_level);
        //if (result.data.access_level === "moderador") {
        const usersResult = await fetchUsers.list();
        if (usersResult.ok && usersResult.data) {
          const options = Object.values(usersResult.data)
            .filter(u => u.active)
            .map(u => ({ value: u.id, label: u.fullname || u.email }));
          options.unshift({ value: null, label: "Todos" });
          setUserFiltersOptions(options);
        }
        //}
      } else if (result.message) {
        ToastMessage({ cod: "e", texto: result.message || "Erro ao carregar nível de acesso FE" });
        console.error("setAccessLevelFunc: Erro da API ao carregar nível de acesso:", result.message);
      }
    } catch (error) {
      console.error("setAccessLevelFunc: Erro ao carregar nível de acesso:", error);
    }
  }, [fetchUsers]);

  useEffect(() => {
    const initialLoad = async () => {
      try {
        await setAccessLevelFunc();
        await loadAppointments();
      } catch (error) {
        console.error("useEffect [initialLoad]: Erro no carregamento inicial:", error);
      }
    };
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Este useEffect só roda uma vez na montagem inicial

  useEffect(() => {
    // Ignora a primeira execução (já é feita pelo initialLoad)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const reloadData = async () => {
      try {
        await loadAppointments();
      } catch (error) {
        console.error("useEffect [atualizar, loadAppointments, currentCalendarMonth]: Erro ao recarregar dados:", error);
      }
    };
    reloadData();
  }, [atualizar, loadAppointments, currentCalendarMonth]);

  const handleMonthChange = useCallback((date) => {
    if (!date) return;
    const normalizedDate = new Date(date);
    setCurrentCalendarMonth(
      new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), 1)
    );
  }, []);

  const handleDateClick = useCallback(async (date) => {
    setSelectedDate([date?.date] || date);
    setSelectedEvent(null);
    const hoje = new Date();
    const hojeUTC = new Date(Date.UTC(hoje.getUTCFullYear(), hoje.getUTCMonth(), hoje.getUTCDate()));

    if (ChecarData({ data: date.date }) || isSameDay(hojeUTC, date.date)) {
      setModalAgendamentoOpen(true);
    } else {
      ToastMessage({ cod: "e", texto: "Não é possível marcar um agendamento em uma data anterior a hoje." });
    }
  }, []);

  const handleEventClick = useCallback((event) => {
    if (isInt(event)) {
      const appointment = appointments[event];
      if (!appointment) {
        return;
      }
      const simularEventoSelecionado = {
        event: {
          id: appointment.id,
          title: appointment.title,
          extendedProps: {
            description: appointment.description,
            services: appointment.services,
            customer: appointment.customer,
            user: appointment.user
          },
          start: new Date(appointment.initial_time),
          end: new Date(appointment.final_time),
        }
      };
      setSelectedEvent(simularEventoSelecionado);
      setSelectedDate([appointment.initial_time]);
    } else {
      setSelectedDate([event.event.start]);
      setSelectedEvent(event);
    }
    setModalAgendamentoOpen(true);
  }, [appointments]);

  const handleSaveAppointment = useCallback(async (appointmentData) => {
    try {
      let response;
      if (appointmentData.id) {
        response = await fetchAppointments.update(appointmentData.id, appointmentData);
        if (response.ok) {
          ToastMessage({ cod: "s", texto: "Agendamento atualizado com sucesso" });
          setModalAgendamentoOpen(false);
          setAtualizar(prev => !prev); // Dispara a recarga de dados
        } else if (response.message) {
          ToastMessage({ cod: "e", texto: response.message });
          console.error("handleSaveAppointment: Erro ao atualizar agendamento:", response.message);
        } else {
          ToastMessage({ cod: "e", texto: "Erro ao atualizar agendamento. FE" });
          console.error("handleSaveAppointment: Erro desconhecido ao atualizar agendamento.");
        }
      } else {
        response = await fetchAppointments.createBulk(appointmentData);
        if (response.ok) {
          ToastMessage({ cod: "s", texto: "Agendamento criado com sucesso" });
          setModalAgendamentoOpen(false);
          setAtualizar(prev => !prev); // Dispara a recarga de dados
        } else if (response.message) {
          ToastMessage({ cod: "e", texto: response.message });
          console.error("handleSaveAppointment: Erro ao criar agendamento:", response.message);
          if (response.result?.card_conflict) {
            ToastMessage({ cod: "e", texto: "Conflito de horário com o agendamento de titulo: '" + appointments[response.result.card_conflict]?.title + "'. Por favor, escolha outro horário." });
          }
        } else {
          ToastMessage({ cod: "e", texto: "Erro ao criar agendamento. FE" });
          console.error("handleSaveAppointment: Erro desconhecido ao criar agendamento.");
        }
      }
    } catch (error) {
      console.error("handleSaveAppointment: Erro ao salvar agendamento:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao salvar o agendamento." });
    }
  }, [fetchAppointments, appointments]);

  const handleDeleteAppointment = useCallback(async (id) => {
    try {
      const confirmed = await modalRef.current.showModal();
      if (confirmed) {
        const response = await fetchAppointments.inactivate(id);
        setModalAgendamentoOpen(false);
        setAtualizar(prev => !prev); // Dispara a recarga de dados
        if (response.ok) {
          ToastMessage({ cod: "s", texto: "Agendamento excluído com sucesso" });
        } else if (response.message) {
          ToastMessage({ cod: "e", texto: response.message });
          console.error("handleDeleteAppointment: Erro ao excluir agendamento:", response.message);
        } else {
          ToastMessage({ cod: "e", texto: "Erro ao excluir agendamento. FE" });
          console.error("handleDeleteAppointment: Erro desconhecido ao excluir agendamento.");
        }
      } else {
      }
    } catch (error) {
      console.error("handleDeleteAppointment: Erro ao excluir agendamento:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao excluir o agendamento." });
    }
  }, [fetchAppointments]);

  const mudarMenu = useCallback(() => {
    if (menuAgendamentosAtivo) {
      setAgendamentosActive(false);
      setTimeout(() => setShowAgendamentos(false), 300);
    } else {
      setShowAgendamentos(true);
      setTimeout(() => setAgendamentosActive(true), 10);
    }
  }, [menuAgendamentosAtivo]);

  const dataForSearch = useMemo(() => {
    return Object.values(appointments).map((appointment) => ({
      label: appointment.title,
      options: [
        { value: appointment.id, label: `Titulo: ${appointment.title}` },
        { value: appointment.id, label: `Descrição: ${appointment.description}` },
        { value: appointment.id, label: `Cliente: ${appointment.customer}` },
        { value: appointment.id, label: `Serviços: ${Object.values(appointment.services || {}).map(service => service.name).join(", ")}` },
        { value: appointment.id, label: `Prestador: ${appointment.user?.fullname || ""}` },
        { value: appointment.id, label: `Data: ${new Date(appointment.initial_time).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}` },
        { value: appointment.id, label: `Hora: ${new Date(appointment.initial_time).toLocaleTimeString('pt-BR', { timeZone: 'UTC', hour12: false })}` },
      ]
    }));
  }, [appointments]);

  const optionHandle = useCallback((selectedOption) => {
    if (selectedOption) {
      handleEventClick(selectedOption.value);
    }
  }, [handleEventClick]);

  useEffect(() => {
    let timeoutId;
    const atualizarLargura = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setLarguraTela(window.innerWidth);
      }, 150);
    };
    window.addEventListener('resize', atualizarLargura);
    return () => {
      window.removeEventListener('resize', atualizarLargura);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleServicoClick = useCallback(() => {
    setModalServicoOpen(true);
    setServicoData(null);
  }, []);

  const handleInativarFuncionarioClick = useCallback(() => {
    setModalGerenciarUsuarioOpen(true);
  }, []);

  const handleSaveServico = useCallback(async (servico) => {
    try {
      const newService = {
        id: servico.id,
        active: servico.status,
        name: servico.nome,
        description: servico.descricao,
        price: parseFloat(servico.preco),
        time_service: servico.prazo.length !== 8 ? servico.prazo + ":00" : servico.prazo,
        color: servico.cor,
      };
      const result = newService.id > 0
        ? await fetchServices.update(newService.id, newService)
        : await fetchServices.create(newService);

      if (result.ok) {
        setModalServicoOpen(false);
        ToastMessage({ cod: "s", texto: "Dados registrados com sucesso." });
        setAtualizar(prev => !prev); // Dispara a recarga de dados
      } else if (result.message) {
        ToastMessage({ cod: "e", texto: result.message });
        console.error("handleSaveServico: Erro ao salvar serviço:", result.message);
      } else {
        ToastMessage({ cod: "e", texto: "Erro ao cadastrar serviço. FE" });
        console.error("handleSaveServico: Erro desconhecido ao salvar serviço.");
      }
    } catch (error) {
      console.error("handleSaveServico: Erro ao salvar serviço:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao salvar o serviço." });
    }
  }, [fetchServices]);

  const handleUsuarioClick = useCallback(() => {
    setModalUsuarioOpen(true);
    setUsuarioData(null);
  }, []);

  const handleDashboardclick = useCallback(() => {
    navigate("/admin/dashboard");
  }, [navigate]);

  const handlePerfilClick = useCallback(async () => {
    try {
      const result = await fetchUsers.getCurrentUser();
      if (result.ok) {
        const userData = {
          nome: result.data.fullname || "",
          email: result.data.email || "",
          telefone: result.data.phone || "",
          pais: result.data.country || "",
          senha: result.data.password || "",
          confirmarSenha: result.data.password || "",
          novasenha: "",
        };
        setUsuarioData(userData);
      } else if (result.message) {
        ToastMessage({ cod: "e", texto: result.message || "Erro ao carregar dados do usuário FE" });
        console.error("handlePerfilClick: Erro ao carregar dados do usuário:", result.message);
      }
    } catch (error) {
      console.error("handlePerfilClick: Erro ao carregar dados do usuário:", error);
      ToastMessage({ cod: "e", texto: "Erro ao carregar os dados do perfil." });
    }
    setModalUsuarioOpen(true);
  }, [fetchUsers]);

  const handleGerenciarClick = useCallback(async () => {
    setModalGerenciarUsuarioOpen(true);
  }, []);

  const handleSaveUsuario = useCallback(async (usuario) => {
    try {
      const newUsuario = {
        fullname: usuario.fullname,
        email: usuario.email,
        phone: usuario.phone,
        country: usuario.country,
        password: usuario.password,
        active: true
      };
      const result = await fetchUsers.create(newUsuario);
      if (result.ok) {
        ToastMessage({ cod: "s", texto: "Usuário criado com sucesso" });
        setModalUsuarioOpen(false);
        setAtualizar(prev => !prev); // Dispara a recarga de dados
      } else if (result.message) {
        // Return error to be caught by the modal form
        return { error: result.message };
      } else {
        ToastMessage({ cod: "e", texto: "Erro ao criar usuário FE" });
        console.error("handleSaveUsuario: Erro desconhecido ao criar usuário.");
        return { error: "Erro desconhecido ao criar usuário." };
      }
    } catch (error) {
      console.error("handleSaveUsuario: Erro ao salvar usuário:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao criar o usuário." });
      return { error: "Ocorreu um erro ao criar o usuário." };
    }
  }, [fetchUsers]);

  const handleCloseModal = useCallback((arg = "") => {
    if (arg !== "esperar") {
      setModalAgendamentoOpen(false);
      setPrefillData(null); // Limpa os dados de pré-preenchimento
    } else {
    }
  }, []);

  const handleBulkEdit = useCallback(() => {
    setModalAgendamentoOpen(false);
    setBulkModalOpen(true);
  }, []);

  const handleCloseBulkModal = useCallback((shouldRefresh) => {
    setBulkModalOpen(false);
    if (shouldRefresh) {
      setAtualizar(prev => !prev);
    }
  }, []);

  // Callbacks memoizados para fechar modais
  const handleCloseServicoModal = useCallback(() => setModalServicoOpen(false), []);
  const handleCloseUsuarioModal = useCallback(() => setModalUsuarioOpen(false), []);
  const handleCloseGerenciarModal = useCallback(() => setModalGerenciarUsuarioOpen(false), []);

  const handleEditProfileUsuario = useCallback(async (usuario) => {
    try {
      let editUsuario = {
        fullname: usuario.fullname,
        email: usuario.email,
        phone: usuario.phone,
        country: usuario.country,
        password: usuario.password,
        newpassword: usuario.newpassword,
        confirmpassword: usuario.confirmpassword,
        active: true
      };
      const result = await fetchUsers.updateCurrent(editUsuario);
      if (result.ok) {
        setModalUsuarioOpen(false);
        ToastMessage({ cod: "s", texto: "Usuário alterado com sucesso" });
        setAtualizar(prev => !prev); // Dispara a recarga de dados
      } else if (result.message) {
        return { error: result.message };
      } else {
        ToastMessage({ cod: "e", texto: "Erro ao alterar usuário FE" });
        console.error("handleEditProfileUsuario: Erro desconhecido ao alterar usuário.");
        return { error: "Erro desconhecido ao alterar usuário." };
      }
    } catch (error) {
      console.error("handleEditProfileUsuario: Erro ao editar usuário:", error);
      ToastMessage({ cod: "e", texto: "Ocorreu um erro ao alterar o usuário." });
      return { error: "Ocorreu um erro ao alterar o usuário." };
    }
  }, [fetchUsers]);

  const handleInativarUsuario = useCallback(async (usuario) => {
    try {
      const result = await fetchUsers.inativeUser(usuario);
      if (result.ok) {
        ToastMessage({ cod: "s", texto: "Usuário inativado com sucesso!" });
        setModalGerenciarUsuarioOpen(false);
        setAtualizar(prev => !prev); // Dispara a recarga de dados
      } else if (result.message) {
        ToastMessage({ cod: "e", texto: result.message });
        console.error("handleInativarUsuario: Erro ao inativar usuário:", result.message);
      } else {
        ToastMessage({ cod: "e", texto: "Erro ao inativar usuário. FE" });
        console.error("handleInativarUsuario: Erro desconhecido ao inativar usuário.");
      }
    } catch (error) {
      ToastMessage({ cod: "e", texto: "Erro ao inativar usuário. Tente novamente." });
      console.error("handleInativarUsuario: Erro:", error);
    }
  }, [fetchUsers]);

  useImperativeHandle(ref, () => ({
    handleServicoClick,
    handleUsuarioClick,
    handlePerfilClick,
    handleGerenciarClick,
    handleInativarFuncionarioClick,
    accessLevel
  }), [handleServicoClick, handleUsuarioClick, handlePerfilClick, handleGerenciarClick, handleInativarFuncionarioClick, accessLevel]);

  useEffect(() => {
    const handleMenuAction = (event) => {
      const { action } = event.detail;
      switch (action) {
        case 'servico':
          handleServicoClick();
          break;
        case 'usuario':
          handleUsuarioClick();
          break;
        case 'perfil':
          handlePerfilClick();
          break;
        case 'gerenciar':
          handleGerenciarClick();
          break;
        case 'inativar_funcionario':
          handleInativarFuncionarioClick();
          break;
        default:
          break;
      }
    };
    window.addEventListener('menuAction', handleMenuAction);
    return () => {
      window.removeEventListener('menuAction', handleMenuAction);
    };
  }, [handleServicoClick, handleUsuarioClick, handlePerfilClick, handleGerenciarClick, handleInativarFuncionarioClick]);

  const buttonMenuAgendamentos = useCallback(() => {
    return <button
      type="button"
      className={`fc fc-button-primary ${style.menuBtn}`}
      onClick={mudarMenu}
    >
      {showAgendamentos ? "Fechar" : "Abrir"} Menu
    </button>
  }, [showAgendamentos, mudarMenu]);

  const customSelectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      border: '1px solid #ccc',
      borderRadius: '20px',
      paddingLeft: larguraTela <= 1024 && !isUserFilterFocused ? '0px' : '10px',
      paddingRight: larguraTela <= 1024 && !isUserFilterFocused ? '0px' : '10px',
      cursor: 'pointer',
    minWidth: larguraTela <= 1024 ? (isUserFilterFocused ? '100%' : '80px') : '200px',
      minHeight: '38px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: larguraTela <= 1024 && !isUserFilterFocused ? '0px 4px' : '2px 8px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
    }),
    indicatorSeparator: (base) => ({
      ...base,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      zIndex: 9999,
    }),
    option: (base) => ({
      ...base,
      color: 'black',
    }),
    singleValue: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  }), [larguraTela, isUserFilterFocused]);

  const handleUserFilterChange = useCallback((option) => {
    setSelectedUserFilter(option);
    setAtualizar(prev => !prev);
  }, []);

  const mainContent = useMemo(() => (
    <div className={style.main_content}>
      <div className={`${style.topBarContainer} ${isSearchFocused || isUserFilterFocused ? style.topBarContainerFocused : ''}`}>
        <SearchBar 
          data={dataForSearch} 
          className={`${style.searchBar} ${isSearchFocused ? style.searchBarFocused : (larguraTela <= 1024 ? style.searchBarCollapsed : '')} ${isUserFilterFocused ? style.hiddenOnMobile : ''}`} 
          optionHandle={optionHandle} 
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          isMobileCollapsed={larguraTela <= 1024 && !isSearchFocused}
        />
        {["admin", "mod", "moderador"].includes(accessLevel) && (
          <div className={`${style.userFilterWrapper} ${isUserFilterFocused ? style.userFilterFocused : (larguraTela <= 1024 ? style.userFilterCollapsed : '')} ${isSearchFocused ? style.hiddenOnMobile : ''}`}>
            <Select
              options={userFiltersOptions}
              value={selectedUserFilter}
              onChange={handleUserFilterChange}
              styles={customSelectStyles}
              placeholder="Selecionar Usuário"
              menuPortalTarget={document.body}
              onFocus={() => setIsUserFilterFocused(true)}
              onBlur={() => setIsUserFilterFocused(false)}
            />
          </div>
        )}
      </div>
      <div className={style.calendario}>
        <Calendario
          appointments={appointments}
          onDateClick={handleDateClick}
          onEventClick={handleEventClick}
          onMonthChange={handleMonthChange}
        />
      </div>
      {buttonMenuAgendamentos()}
    </div>
  ), [appointments, dataForSearch, handleDateClick, handleEventClick, optionHandle, buttonMenuAgendamentos, handleMonthChange, accessLevel, userFiltersOptions, selectedUserFilter, handleUserFilterChange, customSelectStyles, isSearchFocused, isUserFilterFocused, larguraTela]);

  const menuAgendamentosContent = useMemo(() => (
    <div className={`${style.menuAgendamentos} ${menuAgendamentosAtivo ? style.menuAgendamentos_ativo : ''}`}>
      <Rightbar
        appointments={appointments}
        onDeleteAppointment={handleDeleteAppointment}
        onDateClick={handleDateClick}
        onAppointmentClick={handleEventClick}
      />
      {buttonMenuAgendamentos()}
    </div>
  ), [appointments, handleDeleteAppointment, handleDateClick, handleEventClick, menuAgendamentosAtivo, buttonMenuAgendamentos]);

  return (
    <>
      <ModalConfirmaExclusao
        ref={modalRef}
        onConfirm={null}
        onCancel={null}
      />

      <div className={style.container + ' CalendarioApp'}>
        {(showAgendamentos && larguraTela <= 1024) && (
          <div className={style.menu_blur} onClick={mudarMenu} />
        )}

        {larguraTela > 1024 && (
          <Sidebar
            onServicoClick={handleServicoClick}
            onUserClick={handleUsuarioClick}
            onPerfilClick={handlePerfilClick}
            onGerenciarClick={handleGerenciarClick}
            onDashboardClick={handleDashboardclick}
            accessLevel={accessLevel}
          />
        )}

        {((!showAgendamentos && larguraTela <= 1024) || larguraTela > 1024) && mainContent}
        {(showAgendamentos || larguraTela > 1024) && menuAgendamentosContent}

        {modalAgendamentoOpen && (
          <AgendamentoModal
            onClose={handleCloseModal}
            onSave={handleSaveAppointment}
            onDelete={handleDeleteAppointment}
            selectedDate={selectedDate}
            selectedEvent={selectedEvent}
            onBulkEdit={handleBulkEdit}
            prefillData={prefillData}
          />
        )}

        {isBulkModalOpen && (
          <BulkAppointmentsModal
            isOpen={isBulkModalOpen}
            onClose={handleCloseBulkModal}
            groupId={selectedEvent?.event?.extendedProps?.full_appointment?.group}
          />
        )}

        {modalServicoOpen && (
          <ServicoModal
            onClose={handleCloseServicoModal}
            onSave={handleSaveServico}
            initialData={servicoData}
          />
        )}

        {modalUsuarioOpen && (
          <UsuarioModal
            onClose={handleCloseUsuarioModal}
            onSave={handleSaveUsuario}
            onEditProfile={handleEditProfileUsuario}
            initialData={usuarioData}
          />
        )}

        {modalGerenciarUsuarioOpen && (
          <UsersManage
            onSave={handleInativarUsuario}
            onClose={handleCloseGerenciarModal}
            initialData={fetchUsers}
          />
        )}

        {load.isLoading && (
          <div id="loading-overlay">
            <div>
              <div id="loading-spinner"></div>
              <p>{load.reason}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default CalendarioApp;